import React, { Fragment } from "react";
import IncomeAttributeLookupObject from "../../types/lookups/IncomeAttributeLookupObject";
import { useTranslation } from "react-i18next";
import Input from "../../components/common/Input/Input";
import Asterisk from "../../components/common/Asterisk/Asterisk";
import ToolTip from "../../components/common/ToolTip/ToolTip";
import { useSelector } from "react-redux";
import { ProfileOverviewState } from "../../redux/reducers/getProfile/getProfileOverviewReducer";
import { FormError, getFormErrorByName, getFormErrorMessageByName, hasError, incomeAttributeDescMatcher, incomeAttributeMatcher, MatchingStrategy, useFormErrorContext } from "../context/FormErrorContext";
import AIRPNumberInput from "../../components/common/AIRPNumberInput/AIRPNumberInput";
import { useShowIncomeLabel } from "../ShowIncomeLabelContext";
import { selectMenu, selectProfileGetOverview } from "../../redux/selectors/exportSelectorPaths";
import { useReadOnly } from "../ReadOnlyContext";
import { shouldDisplayPreviousTwoYearAttributes, shouldDisplayPreviousYearAttributes } from "../previousIncomeAttributes";

interface AttributeTableFragmentProps {
  attribute: IncomeAttributeLookupObject,
  data: Record<string, unknown> | undefined,
  showSubheading: boolean,
  isOtherLabel: boolean,
  hasInputLabel: boolean,
  isPlaceHolderWithAttrDesc?: boolean,
  isRequiredField: boolean,
  isReplacementReserve?: boolean,
  isCapitalCostsOrReplacementReserve?: boolean,
  isProfileAttribute?: boolean,
  handleCommentChange?: (value: string, name: string) => void,
  valueChangeHandler?: (newAmt: number | undefined | null, name: string, oldAmt?: number | undefined | null) => void,
  errors?: FormError[],
  fieldType?: "percent" | "integer",
  tooltip?: string,
  errorMatcher?: MatchingStrategy,
  maxVal?: boolean,
  hideTooltip?: boolean,
  narrow?: boolean
}
export default function IncomeAttributeTableFragmentGenerator(props: AttributeTableFragmentProps): JSX.Element {
  const { i18n } = useTranslation();
  const { t } = useTranslation("pie");
  const profileOverview: ProfileOverviewState = useSelector(selectProfileGetOverview);
  const menuState = useSelector(selectMenu);
  const activeMenuName = menuState.activeItem.name;
  const { attribute, data, showSubheading, isOtherLabel, hasInputLabel, isRequiredField, isReplacementReserve, isCapitalCostsOrReplacementReserve } = props;
  const { errors, removeError } = useFormErrorContext()
  const { showIncomeLabel } = useShowIncomeLabel()
  const { isReadOnly } = useReadOnly()
  const matcher = props.errorMatcher ? props.errorMatcher : incomeAttributeMatcher
  let attrDesc = "";
  i18n.language === 'fr' ? attrDesc = attribute.incomeAttributeFrenchDescription : attrDesc = attribute.incomeAttributeDescription
  let tooltip = props.tooltip ? props.tooltip : isCapitalCostsOrReplacementReserve ? t("tooltip.capitalCostsOrReplacementReserve." + attribute.incomeAttributeCode) : t("tooltip." + profileOverview.profile.profile_type_code + "." + attribute.incomeAttributeCode);
  if (tooltip.startsWith("tooltip.")) {
    tooltip = attrDesc;
  }

  const renderInput = () => {
    switch (props.fieldType) {
      case 'percent':
        return <AIRPNumberInput
          formatType="decimal"
          textAlign="right"
          name={attribute.incomeAttributeCode}
          initialValue={data && data["percent"] as number}
          symbol="%"
          max={100}
          min={-100}
          errorMatcher={matcher}
          ariaLabel={`Percentage, ${attrDesc}`}
          onChange={(value, lastValue) => {
            const name = props.isProfileAttribute ? `${attribute.incomeAttributeCode}` : "income-attribute-" + attribute.incomeAttributeCode
            props.valueChangeHandler?.(value, name, lastValue)
          }} />
      case 'integer':
        return <AIRPNumberInput
          textAlign="right"
          name={attribute.incomeAttributeCode}
          initialValue={data && data["value"] as number}
          decimals={false}
          errorMatcher={matcher}
          ariaLabel={`Amount, ${attrDesc}`}
          onChange={(value, lastValue) => {
            const name = props.isProfileAttribute ? `${attribute.incomeAttributeCode}` : "income-attribute-" + attribute.incomeAttributeCode
            props.valueChangeHandler?.(value, name, lastValue)
          }} />
      default:
        return <></>
    }
  }
  return (
    <Fragment key={attribute.incomeAttributeCode}>
      {showSubheading && (
        <tr className="sub-th">
          <td colSpan={2} className='label' headers="attribute-label">
            {isOtherLabel ? t("other") : attrDesc}
          </td>
        </tr>
      )}
      <tr data-attribute-code={attribute.incomeAttributeCode}>
        {hasInputLabel ?
          (
            props.isPlaceHolderWithAttrDesc ?
              (
                showIncomeLabel &&
                <td headers="income-attribute-comment-header" data-income-attribute-code={attribute.incomeAttributeCode} className={`${(hasError(errors, attribute.incomeAttributeCode, incomeAttributeDescMatcher) ? "table-error" : "") + ((shouldDisplayPreviousYearAttributes() || shouldDisplayPreviousTwoYearAttributes()) ? " narrow-label-with-input" : "")}`}>
                  <ToolTip className="attribute-comment-wrapper" contents={
                    <Input type="text" name={"income-attribute-comment-" + attribute.incomeAttributeCode}
                      id={attribute.incomeAttributeCode}
                      ariaLabel={attrDesc + (attrDesc ? " " : "") + t("comment")}
                      placeholder={t("comment")}
                      value={data && data["description"] as string}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        removeError(getFormErrorByName(errors, attribute.incomeAttributeCode, incomeAttributeDescMatcher)?.primaryKey as string)
                        if (props.handleCommentChange) {
                          props.handleCommentChange(e.target.value, e.target.name)
                        }
                      }}
                      autoComplete="off"
                      hideErrorCross={true}
                      errorMessage={getFormErrorMessageByName(errors, attribute.incomeAttributeCode, incomeAttributeDescMatcher)}
                      errorMatcher={incomeAttributeDescMatcher}
                      useIdForError={true}
                    />
                  }>
                    {tooltip}
                  </ToolTip>
                </td>
              )
              :
              (
                showIncomeLabel &&
                <td headers="income-attribute-comment-header" data-income-attribute-code={attribute.incomeAttributeCode} className={`${(hasError(errors, attribute.incomeAttributeCode, incomeAttributeDescMatcher) ? "table-error" : "") + ((shouldDisplayPreviousYearAttributes() || shouldDisplayPreviousTwoYearAttributes()) ? " narrow-label-with-input" : "")}`}>
                  <ToolTip className="attribute-comment-wrapper" contents={
                    <Input type="text" name={"income-attribute-comment-" + attribute.incomeAttributeCode}
                      id={attribute.incomeAttributeCode}
                      ariaLabel={attrDesc + (attrDesc ? " " : "") + t("comment")}
                      placeholder={t("comment")}
                      value={data && data["description"] as string}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        removeError(getFormErrorByName(errors, attribute.incomeAttributeCode, incomeAttributeDescMatcher)?.primaryKey as string)
                        if (props.handleCommentChange) {
                          props.handleCommentChange(e.target.value, e.target.name)
                        }
                      }}
                      autoComplete="off"
                      hideErrorCross={true}
                      errorMessage={getFormErrorMessageByName(errors, attribute.incomeAttributeCode, incomeAttributeDescMatcher)}
                      errorMatcher={incomeAttributeDescMatcher}
                      useIdForError={true}
                    />}>
                    {tooltip}
                  </ToolTip>
                </td>
              )
          )
          :
          showIncomeLabel &&
          (<td className={"label" + ((shouldDisplayPreviousYearAttributes() || shouldDisplayPreviousTwoYearAttributes()) ? " narrow-label" : "")} data-income-attribute-code={attribute.incomeAttributeCode}>
            {props.hideTooltip ? i18n.language === 'fr' ? attribute.incomeAttributeFrenchDescription : attribute.incomeAttributeDescription :
              <ToolTip contents={(i18n.language === 'fr' ? attribute.incomeAttributeFrenchDescription : attribute.incomeAttributeDescription)}>
                {tooltip}
              </ToolTip>
            }
            {isRequiredField && <>&nbsp;<Asterisk /></>}
          </td>)}
        <td headers="income-attribute-value-header" data-income-attribute-code={attribute.incomeAttributeCode} className={`${showIncomeLabel ? "value" : "previous-value"} ${!isReadOnly && hasError(errors, attribute.incomeAttributeCode, matcher) ? "table-error" : ""}`}>
          {
            props.fieldType ? renderInput() :
              <AIRPNumberInput
                textAlign="right"
                name={attribute.incomeAttributeCode}
                initialValue={data && data["value"] as number}
                symbol="$"
                errorMatcher={matcher}
                ariaLabel={`Amount, ${attrDesc}`}
                onChange={(value, lastValue) => {
                  const name = props.isProfileAttribute ? `${attribute.incomeAttributeCode}` : "income-attribute-" + attribute.incomeAttributeCode
                  props.valueChangeHandler?.(value, name, lastValue)
                }} />
          }
        </td>
      </tr>

      {isReplacementReserve && (
        <>
          <tr className="sub-th">
            <td colSpan={showIncomeLabel ? 2 : 1} className='label' headers="attribute-label">
              &nbsp;
            </td>
          </tr>
          <tr title={t("tooltip." + activeMenuName + ".items")}>
            {showIncomeLabel && <th className='label' headers="attribute-label">
              {t(activeMenuName + ".items")}
            </th>}
            <th className="text-center">
              {""}
            </th>
          </tr>
        </>
      )
      }
    </Fragment>
  );
}